// libs
import { KeyboardArrowLeft, KeyboardArrowRight } from '@mui/icons-material';
import { Box, Typography } from '@mui/material';
import addDays from 'date-fns/addDays';
import differenceInDays from 'date-fns/differenceInDays';
import differenceInHours from 'date-fns/differenceInHours';
import format from 'date-fns/format';
import isSameDay from 'date-fns/isSameDay';
import set from 'date-fns/set';
import subDays from 'date-fns/subDays';
import React, { useCallback, useMemo, useRef, useState } from 'react';
import AliceCarousel from 'react-alice-carousel';
import 'react-alice-carousel/lib/alice-carousel.css';
import {
  OfficeHoursUtils,
  officeClosedDates
} from '../../utils/Constants/officeHours';
import Chip from '../Chip';

// styles
import styles from './styles';
import { dynamicImport } from '../../utils/reactUtils';

const Tooltips = dynamicImport(() => import('../Tooltip'));

export interface LibHorizontalDateSliderProps {
  disabledDates?: string[];
  value: Date;
  onChange: (d: Date) => void;
  timeSelected: Date[];
  hasPrevDates?: boolean;
  lowerLimitDate?: boolean;
  onSelectTime?: (timeDate: Date[]) => void;
  showWarning?: (show: boolean) => void;
  hasTimeError?: boolean;
}

function getSubDatesByRange(date: Date, range = 10) {
  const arr = [];

  for (let i = 0; i < range; i += 1) {
    arr.unshift(subDays(date, i + 1));
  }

  return arr;
}

function getAddDatesByRange(date: Date, range = 10) {
  const arr = [];

  for (let i = 0; i < range; i += 1) {
    arr.push(addDays(date, i + 1));
  }

  return arr;
}

const get12H = (d: number) => {
  if (!d) {
    return '';
  }

  const hours = d % 12 || 12;
  const AmOrPm = d >= 12 ? 'PM' : 'AM';

  return `${hours}${AmOrPm}`;
};

const isBelow12 = (d: Date, h: number) => {
  const nDate = set(d, { hours: h });

  return differenceInHours(nDate, new Date()) < 12;
};

const dateHourFormatString = 'dd/MM/yyyy H';

const isSelectedTime = (
  activeDate: Date,
  chip1: number,
  chip2: number,
  selectedTime1: Date,
  selectedTime2: Date
) => {
  const chip1Hours = format(
    set(activeDate, { hours: chip1 }),
    dateHourFormatString
  );
  const chip2Hours = format(
    set(activeDate, { hours: chip2 }),
    dateHourFormatString
  );
  const selectedTimeHour1 = selectedTime1
    ? format(selectedTime1, dateHourFormatString)
    : '';
  const selectedTimeHour2 = selectedTime2
    ? format(selectedTime2, dateHourFormatString)
    : '';

  return chip1Hours === selectedTimeHour1 && chip2Hours === selectedTimeHour2;
};

const dateFormatString = 'yyyy-MM-dd';

function LibHorizontalDateSlider({
  disabledDates = officeClosedDates,
  value = new Date(),
  onChange,
  timeSelected,
  onSelectTime,
  hasPrevDates = false,
  lowerLimitDate = true,
  showWarning,
  hasTimeError = false
}: LibHorizontalDateSliderProps) {
  const carousel = useRef<AliceCarousel>(null);

  const previousDates = hasPrevDates
    ? getSubDatesByRange(value)
    : getSubDatesByRange(
        value,
        lowerLimitDate ? Math.abs(differenceInDays(new Date(), value)) + 1 : 1
      );

  const initialArr = [...previousDates, value, ...getAddDatesByRange(value)];

  const [sliderDates, setSliderDates] = useState(initialArr);
  const selectedItemIndex = useMemo(
    () => sliderDates.findIndex((d) => isSameDay(d, value)),
    [sliderDates, value]
  );
  const [isSliding, setIsSliding] = useState(false);
  const isDateDisabled = useCallback(
    (d: Date) => disabledDates.includes(format(d, dateFormatString)),
    [disabledDates]
  );

  const handleChange = (i: number) => {
    setIsSliding(false);

    if (sliderDates.length - 1 <= i + 6) {
      const arr = [
        ...sliderDates,
        ...getAddDatesByRange(sliderDates[sliderDates.length - 1])
      ];

      setSliderDates(arr);
    }

    if (i <= 6 && hasPrevDates) {
      const arr = [...getSubDatesByRange(sliderDates[0]), ...sliderDates];

      setSliderDates(arr);
    }

    onChange(sliderDates[i]);
    handleWarning(sliderDates[i]);
    onSelectTime([]);
  };

  const handleWarning = (d: Date) => {
    const isDayHourEmpty =
      OfficeHoursUtils.getViewingHours(d, disabledDates).length === 0;

    if (showWarning) showWarning(isDayHourEmpty);
  };

  const onItemClick = (
    d: Date,
    e: React.MouseEvent<HTMLButtonElement, MouseEvent>
  ) => {
    const itemIndex = sliderDates.indexOf(d);

    e.preventDefault();
    handleChange(itemIndex);
    handleWarning(d);
  };

  const handleNext = () => {
    setIsSliding(true);
    handleChange(selectedItemIndex + 1);
    handleWarning(sliderDates[selectedItemIndex + 1]);
  };

  const handlePrev = () => {
    setIsSliding(true);
    handleChange(selectedItemIndex - 1);
    handleWarning(sliderDates[selectedItemIndex - 1]);
  };

  const getDateStyles = (d: Date) => {
    if (d.toString() === 'Invalid Date') {
      return styles.dateBoxDisabled;
    }

    if (format(d, dateFormatString) === format(value, dateFormatString)) {
      return styles.selectedBox;
    }

    return styles.dateBox;
  };

  return (
    <Box sx={styles.datePickerBox}>
      <Box sx={styles.datePickerInternalBox}>
        <Box sx={styles.dateCalender}>
          <AliceCarousel
            activeIndex={selectedItemIndex - 1}
            disableDotsControls
            responsive={{ 0: { items: 3 } }}
            renderNextButton={() => (
              <Box
                onClick={isSliding ? undefined : handleNext}
                sx={styles.nextBtn}
              >
                <KeyboardArrowRight />
              </Box>
            )}
            renderPrevButton={() =>
              selectedItemIndex === 1 && !hasPrevDates ? null : (
                <Box
                  onClick={isSliding ? undefined : handlePrev}
                  sx={styles.prevBtn}
                >
                  <KeyboardArrowLeft />
                </Box>
              )
            }
            onSlideChange={() => setIsSliding(true)}
            onSlideChanged={(e) => handleChange(e.item + 1)}
            items={sliderDates.map((d: Date, idx) =>
              !hasPrevDates && idx === 0 ? null : (
                <Box
                  key={format(d, 'yyyy-MM-dd')}
                  sx={styles.dateBoxWrapper}
                  data-value={idx}
                >
                  <Box
                    sx={getDateStyles(d)}
                    component="button"
                    disabled={isDateDisabled(d)}
                    onClick={(e) => onItemClick(d, e)}
                  >
                    <Typography variant="body1" sx={styles.dayText}>
                      {format(d, 'EE')}
                    </Typography>

                    <Typography
                      variant="h2"
                      component="span"
                      sx={styles.dateText}
                    >
                      {format(d, 'dd')}
                    </Typography>

                    <Typography variant="body1" sx={styles.monthText}>
                      {format(d, 'MMM')}
                    </Typography>
                  </Box>
                </Box>
              )
            )}
            ref={carousel}
            ssrSilentMode
          />
        </Box>
      </Box>

      <Box sx={styles.timeBox}>
        {OfficeHoursUtils.getViewingHours(
          sliderDates[selectedItemIndex],
          disabledDates
        ).map((chip) => {
          const activeDate = sliderDates[selectedItemIndex];

          return (
            <Tooltips
              label="Please call to check appointments
            for tomorrow"
              placement="top"
              arrow
              iconEnable
              renderOnTouch={isBelow12(activeDate, chip[0])}
              disableHoverListener={!isBelow12(activeDate, chip[0])}
              key={activeDate.getMilliseconds() + chip[0] + chip[1]}
            >
              <Chip
                label={
                  <Typography sx={styles.timeButton}>
                    {get12H(chip[0])} To {get12H(chip[1])}
                  </Typography>
                }
                tag="text"
                selected={isSelectedTime(
                  activeDate,
                  chip[0],
                  chip[1],
                  timeSelected[0],
                  timeSelected[1]
                )}
                clickable
                disabled={isBelow12(activeDate, chip[0])}
                onClick={() =>
                  onSelectTime([
                    set(activeDate, { hours: chip[0] }),
                    set(activeDate, { hours: chip[1] })
                  ])
                }
                sx={hasTimeError ? styles.errorChip : {}}
              />
            </Tooltips>
          );
        })}
      </Box>
    </Box>
  );
}

export const HorizontalDateSlider = React.memo(LibHorizontalDateSlider);

export default HorizontalDateSlider;
